import { isOnNodeJS } from "common/utils/context";
import type { WebGLRenderer } from "three";
import { Vector2 } from "three";
import { RenderScheduler } from "./RenderScheduler";
import { THREEViewerServices } from "./THREEViewerServices";
import type WebGLContextResourceTracker from "./WebGLContextResourceTracker";

export interface SharedWebGLViewer {
  applyAnimation(): void;
  updateBackbuffer(): boolean; // Return true if the canvas needs to be refreshed.
  refreshCanvas(): void;
  getAnimate(): boolean;
  requestResize(): void;
  clearFrameCache(): void;
  dispose(): void;

  renderOrder: number; // larger numbers are rendered on top.
}

export class SharedWebGL {
  readonly scheduler: RenderScheduler = new RenderScheduler(
    () => this.applyAnimation(),
    () => {
      if (this.viewers.length === 0) {
        return; // Don't do anything if there are no registered viewers (this happens when viewer is using the singleton activeSharedWebGL but is not in canvas/context sharing mode)
      }
      this.updateBackbuffers();
      this.refreshCanvas();
    },
  );
  private viewers: SharedWebGLViewer[] = [];
  readonly services: THREEViewerServices;

  // Canvas size in actual pixels, not CSS pixels.
  private width: number;
  private height: number;
  private canvasRefreshNeeded: boolean = false;
  private canvasRefreshCount: number = 0;

  constructor(
    public readonly canvas: HTMLCanvasElement,
    public readonly glContext: WebGLRenderingContext | WebGL2RenderingContext,
    public readonly renderer: WebGLRenderer,
    public readonly hasWebGL2: boolean,
    public readonly resourceTracker: WebGLContextResourceTracker,
    public readonly resourceTrackerLogInterval?: number,
    private readonly onScreenRenderingDisabled: boolean = false,
  ) {
    this.services = new THREEViewerServices({
      renderer,
      enableLightOffsetMap: true,
      offsetMapLightCount: 8,
      onScreenRenderingDisabled: this.onScreenRenderingDisabled,
    });
    this.width = canvas.width;
    this.height = canvas.height;
    this.scheduler.animate = true; // Always required to react to CSS transform animations, they don't fire events that we could listen to.
  }

  getCanvasRefreshCount(): number {
    return this.canvasRefreshCount;
  }

  applyAnimation() {
    for (const viewer of this.viewers) {
      viewer.applyAnimation();
    }
  }

  updateBackbuffers() {
    // This needs to be done before the viewers update their camera parameters for rendering.
    this.setCanvasPixelSizeFromDOM();

    for (const viewer of this.viewers) {
      viewer.clearFrameCache();
      if (viewer.updateBackbuffer()) {
        this.canvasRefreshNeeded = true;
      }
    }
  }

  requestResize() {
    for (const viewer of this.viewers) {
      viewer.requestResize();
    }
  }

  setCanvasPixelSizeFromDOM(fromContainer?: HTMLElement) {
    const rect = (fromContainer ?? this.canvas).getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      this.width = 1;
      this.height = 1;
    }
    // This math should return the exact device pixels across different modern browsers.
    // It's important to use Math.round(), not Math.floor().
    this.width = Math.round(rect.width * window.devicePixelRatio);
    this.height = Math.round(rect.height * window.devicePixelRatio);
  }

  getSize(): { width: number; height: number } {
    return { width: this.width, height: this.height };
  }

  private refreshCanvasSize() {
    const sizeVec = new Vector2();
    this.renderer.getSize(sizeVec);
    if (sizeVec.x === this.width && sizeVec.y === this.height) {
      return;
    }
    this.renderer.setSize(this.width, this.height, false);
    this.canvasRefreshNeeded = true;
  }

  refreshCanvas() {
    this.refreshCanvasSize();
    if (!this.canvasRefreshNeeded) {
      return;
    }
    this.canvasRefreshNeeded = false;
    this.viewers.sort((a, b) => a.renderOrder - b.renderOrder);
    for (const viewer of this.viewers) {
      viewer.refreshCanvas();
    }
    this.canvasRefreshCount++;
  }

  registerViewer(viewer: SharedWebGLViewer) {
    this.viewers.push(viewer);
  }

  deregisterViewer(viewer: SharedWebGLViewer) {
    const index = this.viewers.indexOf(viewer);
    if (index >= 0) {
      this.viewers.splice(index, 1);
    }
  }

  dispose() {
    this.services.dispose();
    for (const viewer of this.viewers) {
      viewer.dispose();
    }
    this.viewers = [];

    let destroyContext: any;
    if (isOnNodeJS()) {
      destroyContext = this.glContext.getExtension("STACKGL_destroy_context");
    }
    if (!destroyContext) {
      this.renderer.forceContextLoss();
    }
    this.renderer.dispose();
    this.scheduler.dispose();
    if (this.resourceTrackerLogInterval !== undefined) {
      window.clearInterval(this.resourceTrackerLogInterval);
    }

    if (destroyContext) {
      destroyContext.destroy();
    }
  }
}
